/**
 * Helpers for the comparator HPO phenotype list (R5 Clinical History).
 *
 * Comparators persist phenotypes in canonical `Comparator.hpoTerms` (typed
 * loosely as `unknown[]` for round-trip tolerance). These helpers narrow that
 * payload into the editor's `HPOTerm[]` shape and group it for the read-only
 * card — the same Key / Additional / Pertinent Negative buckets the proband's
 * "Indications for Testing" card uses.
 */

import type { HPOTerm } from '@/components/common/hpoTypes'

type HpoType = HPOTerm['type']

/** Reads the SPA `type` (`key` / `additional` / `excluded`) or the IMDB readback `category` (`Key` / ...). */
function normalizeType(type: unknown, category: unknown): HpoType {
  const value = (asString(type) || asString(category)).toLowerCase()
  return value === 'additional' || value === 'excluded' ? value : 'key'
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/** Narrow a canonical `hpoTerms` payload into the editor's `HPOTerm[]` shape. */
export function parseComparatorHpoTerms(value: unknown): HPOTerm[] {
  if (!Array.isArray(value)) return []
  const out: HPOTerm[] = []
  for (const raw of value) {
    if (raw == null || typeof raw !== 'object') continue
    const term = raw as Record<string, unknown>
    const id = asString(term['id']) || asString(term['hpoCode'])
    const name = asString(term['name']) || asString(term['label'])
    if (!id && !name) continue
    out.push({
      id: id || name,
      name: name || id,
      hpoCode: asString(term['hpoCode']) || id,
      type: normalizeType(term['type'], term['category']),
    })
  }
  return out
}

export interface ComparatorHpoGroup {
  title: string
  items: string[]
  isEmpty: boolean
}

/** Group terms into the Key / Additional / Pertinent Negative card layout. */
export function groupComparatorHpoTerms(terms: HPOTerm[]): ComparatorHpoGroup[] {
  const byType = (type: HpoType) => terms.filter((t) => t.type === type).map((t) => t.name)
  const key = byType('key')
  const additional = byType('additional')
  const excluded = byType('excluded')
  return [
    { title: 'Key Phenotypes', items: key, isEmpty: key.length === 0 },
    { title: 'Additional Phenotypes', items: additional, isEmpty: additional.length === 0 },
    { title: 'Pertinent Negative', items: excluded, isEmpty: excluded.length === 0 },
  ]
}
